import { CognitoUtil } from './../authentication/cognito.service';
import { DataSource } from '@angular/cdk/table';
import { SnapshotService } from './../snapshot/snapshot.service';
import { AppConfig } from './../app.config';
import { Observable } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AccountBalanceRow } from './account-balance-row';
import { AccountBalanceService } from './account-balance.service';
import {
    CORRECTION_TYPES,
    Correction,
    AccountBalanceData,
    GetDailyBalancesRequest,
    GetDailyBalanceOutput,
    GetCorrectionsRequest,
    GetCorrectionsOutput,
    CurrencyUtil
} from '@liquitics/transaction';
import { MatDialog } from '@angular/material/dialog';
import { AccountBalanceAdjustmentComponent } from '../correction/account-balance-adjustment/account-balance-adjustment.component';
import { CorrectionService } from '../correction/correction.service';

import * as moment from 'moment';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-account-balance',
    templateUrl: './account-balance.component.html',
    styleUrls: ['./account-balance.component.css']
})
export class AccountBalanceComponent implements OnInit {
    tableData: AccountBalanceRow[] = [];

    constructor(private snapshotService: SnapshotService,
        private accountBalanceService: AccountBalanceService,
        private correctionService: CorrectionService,
        private cognitoUtil: CognitoUtil,
        public dialog: MatDialog,
        private appConfig: AppConfig) {

    }

    ngOnInit() {
        const expectedAccountBalanceData: AccountBalanceData[] = [];
        const currencies: string[] = this.appConfig.activeUserProfile.getCurrencies();
        const balanceRequest = new GetDailyBalancesRequest();
        balanceRequest.date = moment(this.appConfig.getTodayString()).subtract(1, 'day').format('YYYY-MM-DD');
        balanceRequest.currencies = currencies;

        const correctionsRequest = new GetCorrectionsRequest();
        correctionsRequest.date = this.appConfig.getTodayString();
        correctionsRequest.currencies = currencies;

        Promise.all([
            this.accountBalanceService.getDataObservable(balanceRequest).pipe(take(1)).toPromise(),
            this.correctionService.getCorrections(correctionsRequest).pipe(take(1)).toPromise()
        ])
            .then((results: any[]) => {
                const correctedCcyAccounts: Set<string> = new Set();
                const correctionOutput: GetCorrectionsOutput = results[1];
                correctionOutput.corrections
                    .map((correction: Correction) => {
                        correctedCcyAccounts.add(correction.currency + correction.account);
                    });

                const balanceOutput: GetDailyBalanceOutput = results[0];
                const accountData = balanceOutput.balanceData;
                // Hopefully this will resolve itself when moving off of hand created files,
                // but the account contains a break charater at the end (probably because its the last field in the csv)
                accountData.forEach((abd: AccountBalanceData) => {
                    abd.account = abd.account.trim();
                });
                const expectedAccountBalanceRows: AccountBalanceRow[] = accountData
                    .filter((abd: AccountBalanceData) => {
                        return abd.type === 'EXPECTED' && currencies.includes(abd.currency);
                    })
                    .map((abd: AccountBalanceData) => {
                        const row = new AccountBalanceRow();
                        row.currency = abd.currency;
                        row.account = abd.account;
                        row.expectedBalance = abd.amount;
                        row.statementBalance = 0.0;
                        return row;
                    });
                const statementAccountBalanceRows = accountData
                    .filter((abd: AccountBalanceData) => {
                        return abd.type === 'STATEMENT' && currencies.includes(abd.currency);
                    })
                    .map((statementData: AccountBalanceData) => {
                        const row = new AccountBalanceRow();
                        row.currency = statementData.currency;
                        row.account = statementData.account;
                        row.statementBalance = statementData.amount;
                        row.expectedBalance = 0.0;
                        return row;
                    });
                const groupedRows = expectedAccountBalanceRows
                    .concat(statementAccountBalanceRows)
                    .reduce((grouped: any, d: AccountBalanceRow) => {
                        const key = d.account + d.currency;
                        grouped[key] = grouped[key] || [];
                        grouped[key].push(d);
                        return grouped;
                    }, {});
                const mergedAccountBalanceRows = Object.keys(groupedRows)
                    .map(key => {
                        const accountRows: AccountBalanceRow[] = groupedRows[key];
                        return accountRows.reduceRight((prev: AccountBalanceRow, current: AccountBalanceRow) => {
                            current.expectedBalance += prev.expectedBalance;
                            current.statementBalance += prev.statementBalance;

                            return current;
                        });
                    })
                    .filter((row: AccountBalanceRow) => {
                        const accountNames = this.snapshotService.applicationConfiguration.accountNames;
                        return row.account && accountNames[row.account];
                    })
                    .map((row: AccountBalanceRow) => {
                        row.expectedBalance = CurrencyUtil.round(
                            row.expectedBalance, CurrencyUtil.currencyRounding(row.currency));
                        row.statementBalance = CurrencyUtil.round(
                            row.statementBalance, CurrencyUtil.currencyRounding(row.currency));
                        row.balanceDifference = CurrencyUtil.round(
                            row.expectedBalance - row.statementBalance, CurrencyUtil.currencyRounding(row.currency));
                        row.balanceDifferencePercentage = Math.abs(row.balanceDifference / row.expectedBalance) * 100;
                        row.isSignificantDifference = this.isSignificantDifference(row.balanceDifference, row.expectedBalance);
                        row.isStatementBalanceAccepted = correctedCcyAccounts.has(row.currency + row.account);
                        return row;
                    })
                    .sort((row1: AccountBalanceRow, row2: AccountBalanceRow) => {
                        return row2.balanceDifferencePercentage - row1.balanceDifferencePercentage;
                    })
                    ;
                this.tableData = mergedAccountBalanceRows;
            })
            .catch((err: any) => {
                console.log(err);
            })
            ;
    }

    isSignificantDifference(difference: number, total: number): boolean {
        if (total === 0.0) {
            return false;
        }
        return Math.abs(difference / total) > 0.05;
    }

    adjustAccountBalance(row: AccountBalanceRow) {
        const correction = {} as Correction;
        correction.type = CORRECTION_TYPES.BALANCE_CORRECTION;
        correction.settleDate = this.appConfig.getTodayString();
        correction.currency = row.currency;
        correction.account = row.account;
        correction.balanceCorrectionOfficialAmount = row.statementBalance;
        correction.balanceCorrectionCalculatedAmount = row.expectedBalance;
        correction.amount = correction.balanceCorrectionOfficialAmount - correction.balanceCorrectionCalculatedAmount;
        correction.username = this.cognitoUtil.getCurrentUser().getUsername();

        const dialogRef = this.dialog.open(AccountBalanceAdjustmentComponent, {
            width: '50%',
            data: {
                correction: correction
            }
        });
    }
}

